import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { SmallText } from "../../../components/core/Utility";
import "primeicons/primeicons.css";
import bundleIcon from "../../assets/icons/bundle.svg";

const FulfilmentTd = styled.td`
  background-color: #d6f2e0;
  padding: 2px 8px;
  vertical-align: top;
  border: none;
  box-shadow: none;
  line-height: 1.1;
`;

const ReturnedTd = styled.td`
  background-color: #fbe4d7;
  padding: 2px 8px;
  vertical-align: top;
  border: none;
  box-shadow: none;
  line-height: 1.1;
`;

const TableContainer = styled.div`
  font-family: Verizonn-NHG-eTx, Helvetica, Arial, sans-serif;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  /* Custom scrollbar styles */
  ::-webkit-scrollbar {
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const StyledTable = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  font-size: 0.85em;
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
  box-shadow: none;
  border-radius: 0;
  white-space: nowrap;

  th {
    background-color: #ffffff;
    text-align: left;
    font-weight: bold;
    border-bottom: 1px solid #000;
    border-right: 1px solid #e0e0e0;
    border-radius: 0;
    box-shadow: none;
    padding: 8px 20px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: nowrap;
  }

  /* Subscription Name column (2nd column) */
  th:nth-of-type(2),
  td:nth-of-type(2) {
    width: 220px !important;
    max-width: 250px !important;
    min-width: 180px !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }

  /*ordered  column (4nd column) */
  th:nth-of-type(4),
  td:nth-of-type(4) {
    width: 100px !important;
    max-width: 100px !important;
    min-width: 80px !important;
  }

  td {
    vertical-align: top;
    border: none;
    box-shadow: none;
    line-height: 1.1;
    padding: 4px 12px;
    white-space: nowrap;
  }

  tr {
    margin: 0;
    height: auto;
    min-height: 0;
    border: none;
  }

  /* Add bottom border to last row of each bundle */
  tr.bundle-last td,
  tr.bundle-last ${FulfilmentTd}, tr.bundle-last ${ReturnedTd} {
    border-bottom: 2px solid #e0e0e0;
  }
`;

// Define sort order for category types
const getCategoryOrder = (category) => {
  const categoryUpper = category?.toUpperCase() || "";
  switch (categoryUpper) {
    case "CORE":
      return 1;
    case "COMPANION":
      return 2;
    case "FEATURE":
      return 3;
    default:
      return 4;
  }
};

// Define sort order for subcategory types
const getSubCategoryOrder = (subCategory) => {
  const subCategoryUpper = subCategory?.toUpperCase() || "";
  switch (subCategoryUpper) {
    case "MAIN":
      return 1;
    case "ACCESSORY":
      return 2;
    default:
      return 3;
  }
};

// Sort materials by category and subcategory
const sortMaterials = (materials) => {
  if (!materials || materials.length === 0) return [];

  return [...materials].sort((a, b) => {
    // First, sort by category
    const categoryOrderA = getCategoryOrder(a.category);
    const categoryOrderB = getCategoryOrder(b.category);

    if (categoryOrderA !== categoryOrderB) {
      return categoryOrderA - categoryOrderB;
    }

    // If categories are the same, sort by subcategory
    const subCategoryOrderA = getSubCategoryOrder(a.subCategory);
    const subCategoryOrderB = getSubCategoryOrder(b.subCategory);

    return subCategoryOrderA - subCategoryOrderB;
  });
};

// Group materials by subscriptionId
const groupMaterialsBySubscription = (summaryLines) => {
  if (!summaryLines || summaryLines.length === 0) return [];

  // First, sort the materials
  const sortedMaterials = sortMaterials(summaryLines);

  const grouped = sortedMaterials.reduce((acc, material) => {
    const subId = material.subscriptionId || "unknown";
    if (!acc[subId]) {
      acc[subId] = {
        subscriptionId: material.subscriptionId,
        subscription: material.subscription,
        techRefId: material.techRefId,
        materials: [],
      };
    }
    acc[subId].materials.push(material);
    return acc;
  }, {});

  // Convert to array
  const groupedArray = Object.values(grouped);

  // Sort materials within each group
  groupedArray.forEach((group) => {
    group.materials = sortMaterials(group.materials);
  });

  // Sort groups so CORE type subscriptions come first
  groupedArray.sort((a, b) => {
    const aFirstCategory = getCategoryOrder(a.materials[0]?.category);
    const bFirstCategory = getCategoryOrder(b.materials[0]?.category);
    return aFirstCategory - bFirstCategory;
  });

  return groupedArray;
};

export const OrderShippingSummaryGrid = ({ noDataMessage = "No data Available", rows = [] }) => {
  const [sortedRows, setSortedRows] = useState(rows);

  useEffect(() => {
    setSortedRows(rows);
  }, [rows]);

  // Memoize the processed rows with grouped subscriptions
  const processedRows = useMemo(() => {
    if (!sortedRows || sortedRows.length === 0) return [];

    return sortedRows.map((vehicle) => ({
      ...vehicle,
      groupedSubscriptions: groupMaterialsBySubscription(vehicle.summaryLines),
    }));
  }, [sortedRows]);

  if (!sortedRows.length) {
    return <SmallText>{noDataMessage}</SmallText>;
  }

  // Render subscription cell with rowSpan (no border between subscriptions)
  const renderSubscriptionCell = (group, isFirstRow, isLastGroup, isLastBundle) => {
    if (!isFirstRow) return null;

    return (
      <td
        rowSpan={group.materials.length}
        style={{
          borderBottom: isLastGroup && !isLastBundle ? "2px solid #e0e0e0" : "none",
        }}
      >
        <div>
          <SmallText>{group.subscription || "--"}</SmallText>
          <SmallText color="#716F6D">{group.subscriptionId || "--"}</SmallText>
        </div>
        <div style={{ marginTop: "8px" }}>
          <SmallText bold>Tech-Ref ID:</SmallText>
          <SmallText color="#716F6D">{group.techRefId || "--"}</SmallText>
        </div>
      </td>
    );
  };

  const cleanBundleName = (bundleName) => {
    if (!bundleName) return "--";
    // Remove "Bundled Plan |" or "Bundled Plan|" prefix (with or without spaces)
    return bundleName.replace(/^Bundled Plan\s*\|\s*/i, "").trim();
  };

  // Render bundle cell with rowSpan and border
  const renderBundleCell = (vehicle, totalMaterialsInBundle, shouldRender, isLastBundle) => {
    if (!shouldRender) return null;

    return (
      <td
        rowSpan={totalMaterialsInBundle}
        style={{
          borderBottom: isLastBundle ? "none" : "2px solid #e0e0e0",
        }}
      >
        <SmallText>
          <b>{cleanBundleName(vehicle?.bundleName)}</b>
          <div>({vehicle?.bundleId})</div>
        </SmallText>
      </td>
    );
  };

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th>
              <SmallText bold>
                <img src={bundleIcon} width={18} height={18} style={{ display: "inline-flex", verticalAlign: "middle" }} /> Bundle
              </SmallText>
            </th>
            <th>
              <SmallText bold>Subscription</SmallText>
            </th>
            <th>
              <SmallText bold>Category</SmallText>
            </th>
            <th>
              <SmallText bold>Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Fallout</SmallText>
            </th>
            <th>
              <SmallText bold>Remaining</SmallText>
            </th>
            <th>
              <SmallText bold>Submitted</SmallText>
            </th>
            <th>
              <SmallText bold>Created</SmallText>
            </th>
            <th>
              <SmallText bold>Back-Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Packaging</SmallText>
            </th>
            <th>
              <SmallText bold>Shipped</SmallText>
            </th>
            <th>
              <SmallText bold>Delivered</SmallText>
            </th>
            <th>
              <SmallText bold>Exception</SmallText>
            </th>
            <th>
              <SmallText bold>Return to Sender</SmallText>
            </th>
            <th>
              <SmallText bold>Return</SmallText>
            </th>
            <th>
              <SmallText bold>Submitted</SmallText>
            </th>
            <th>
              <SmallText bold>Created</SmallText>
            </th>
            <th>
              <SmallText bold>Back-Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Packaging</SmallText>
            </th>
            <th>
              <SmallText bold>Shipped</SmallText>
            </th>
            <th>
              <SmallText bold>Delivered</SmallText>
            </th>
          </tr>
        </thead>
        <tbody>
          {processedRows.map((vehicle, vehicleIndex) => {
            // Calculate total rows for bundle rowSpan
            const totalMaterialsInBundle = vehicle.groupedSubscriptions.reduce((sum, group) => sum + group.materials.length, 0);

            const isLastBundle = vehicleIndex === processedRows.length - 1;
            let isFirstRowInBundle = true;

            return (
              <React.Fragment key={vehicle?.bundleId}>
                {vehicle.groupedSubscriptions.map((group, groupIndex) =>
                  group.materials.map((material, materialIndex) => {
                    const isFirstRowInGroup = materialIndex === 0;
                    const isLastRowInGroup = materialIndex === group.materials.length - 1;
                    const isLastGroup = groupIndex === vehicle.groupedSubscriptions.length - 1;
                    const shouldRenderBundleCell = isFirstRowInBundle;

                    // After rendering the bundle cell once, set to false
                    if (isFirstRowInBundle) {
                      isFirstRowInBundle = false;
                    }

                    // Determine row class for separator - only bundle level
                    let rowClassName = "";
                    if (isLastRowInGroup && isLastGroup && !isLastBundle) {
                      // Last row of last group in bundle (but not last bundle) - add bundle separator
                      rowClassName = "bundle-last";
                    }

                    return (
                      <tr key={`${vehicle?.bundleId}-${group.subscriptionId}-${materialIndex}`} className={rowClassName}>
                        {/* Bundle ID - rowSpan for entire bundle */}
                        {renderBundleCell(vehicle, totalMaterialsInBundle, shouldRenderBundleCell, isLastBundle)}

                        {/* Subscription Name - rowSpan for subscription group */}
                        {renderSubscriptionCell(group, isFirstRowInGroup, isLastGroup, isLastBundle)}

                        {/* Category */}
                        <td>
                          <SmallText>
                            {material?.category && material?.category !== "-"
                              ? `${material.category}${material?.subCategory ? ` - ${material.subCategory}` : ""}`
                              : material?.subCategory && material?.subCategory !== "-"
                              ? material.subCategory
                              : "--"}
                          </SmallText>
                        </td>

                        {/* Ordered */}
                        <td>
                          <SmallText>{material?.ordered || "--"}</SmallText>
                        </td>

                        {/* Fallout */}
                        <td>
                          <SmallText>{material?.fallout || "--"}</SmallText>
                        </td>

                        {/* Remaining */}
                        <td>
                          <SmallText>{material?.remaining || "--"}</SmallText>
                        </td>

                        {/* Fulfilment columns */}
                        <FulfilmentTd>
                          <SmallText>{material?.submitted || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.created || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.backordered || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.packaging || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.shipped || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.delivered || "--"}</SmallText>
                        </FulfilmentTd>

                        {/* Returned columns */}
                        <ReturnedTd>
                          <SmallText>{material?.exception || "--"}</SmallText>
                        </ReturnedTd>
                        <ReturnedTd>
                          <SmallText>{material?.returnToSender || "--"}</SmallText>
                        </ReturnedTd>
                        <ReturnedTd>
                          <SmallText>{material?.returned || "--"}</SmallText>
                        </ReturnedTd>

                        {/* Return Fulfilment columns */}
                        <FulfilmentTd>
                          <SmallText>{material?.submittedR || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.createdR || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.backorderedR || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.packagingR || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.shippedR || "--"}</SmallText>
                        </FulfilmentTd>
                        <FulfilmentTd>
                          <SmallText>{material?.deliveredR || "--"}</SmallText>
                        </FulfilmentTd>
                      </tr>
                    );
                  })
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};
